import { FC, useEffect, useState, useMemo } from "react";
import { Avatar, Spinner } from "flowbite-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useTemplateService from "../../../services/template";
import { useResourceInfo } from "../../../hooks/useResourceInfo";
import { getTimeZoneDate } from "../../../helpers/data-format";
import { getDefaultUserImage, getDefaultUserName } from "../../../constants/defaultUsers";
import CadenceDataTable from "../../../shared/molecules/CadenceDataTable/CadenceDataTable";
import Options from "./Options";
import { pathWithBreadcrumb } from "../../../utils/pathWithBreadcrumb";
import TruncatedText from "../../../components/common/TruncatedText";

interface TemplatesListProps {
  handleEditTemplate?: any;
}

const TemplatesList: FC<TemplatesListProps> = ({ handleEditTemplate }) => {
  const navigate = useNavigate();
  const {
    templates: reduxTemplates,
    templatesLoading,
    templatesLoadingMore,
    hasMore,
    searchTerm,
  } = useSelector((state: any) => state.templates);
  
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const { getResourceInfo, resourcesLoading } = useResourceInfo();
  const { fetchAllTemplates, fetchMoreTemplates, setSearchTerm } = useTemplateService();

  // Fetch templates on component mount
  useEffect(() => {
    fetchAllTemplates(true, 1, searchTerm);
  }, []);

  const handleLoadMore = () => {
    if (hasMore && !templatesLoadingMore) {
      fetchMoreTemplates();
    }
  };

  const handleOpenLink = (template_id: string)=>{
    navigate(pathWithBreadcrumb({breadcrumb_history: [{name: "Work Items", url: "/workitems"}], current_name: null, url: `/templates/${template_id}`}))
  }

  const columns = useMemo(() => [
    {
      key: "project_name",
      label: "Template Name",
      sortable: true,
      render: (row: any) => (
        
        <span 
          className="cursor-pointer font-medium hover:text-blue-600 transition-colors"
          >
        <TruncatedText text={row.project_name} placement="top-start" className="truncate block">
          {row.project_name}
          </TruncatedText>
        </span>
      ),
      sortValue: (row: any) => row.project_name,
    },
    {
      key: "project_description",
      label: "Description",
      sortable: true,
      render: (row: any) => (
        <span 
          className="truncate block max-w-[300px] cursor-pointer" 
          title={row.project_description}
          onClick={() => handleOpenLink(row.template_id)}
        >
          {row.project_description}
        </span>
      ),
      sortValue: (row: any) => row.project_description,
    },
    {
      key: "created_at",
      label: "Created On",
      sortable: true,
      render: (row: any) => (
        <span 
          className="cursor-pointer"
          onClick={() => handleOpenLink(row.template_id)}
        >
          {String(getTimeZoneDate(row?.created_at, 'MMM DD, YYYY'))}
        </span>
      ),
      sortValue: (row: any) => row.created_at,
    },
    {
      key: "created_by",
      label: "Created By",
      sortable: false,
      render: (row: any) => {
        const defaultUserName = row.created_by ? getDefaultUserName(row.created_by) : null;
        const defaultUserImage = row.created_by ? getDefaultUserImage(row.created_by) : null;
        const creatorInfo = !defaultUserName && row.created_by ? getResourceInfo(row.created_by, row) : null;

        return (
          <div className="flex items-center justify-center h-full w-8 min-h-[32px]">
            {resourcesLoading ? (
              <Spinner size="sm" />
            ) : defaultUserName && defaultUserImage ? (
              <img
                src={defaultUserImage}
                alt={defaultUserName}
                title={defaultUserName}
                className="!w-7 !h-7 !rounded-full object-cover"
              />
            ) : defaultUserName ? (
              <div title={defaultUserName} className="text-xs text-gray-900 dark:text-white font-medium">
                {defaultUserName.substring(0, 2)}
              </div>
            ) : creatorInfo?.image_url ? (
              <Avatar
                img={creatorInfo.image_url}
                rounded
                size="sm"
                title={creatorInfo.name}
                className="!w-7 !h-7 !rounded-full object-cover"
                placeholderInitials={creatorInfo.name?.charAt(0)}
              />
            ) : (
              <span className="text-xs text-gray-900 dark:text-white">-</span>
            )}
          </div>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      render: (row: any) => (
        <Options
          data={row}
          handleClick={handleEditTemplate}
          activeMenuId={activeMenuId}
          setActiveMenuId={setActiveMenuId}
        />
      ),
    },
  ], [activeMenuId, navigate, handleEditTemplate, getResourceInfo, resourcesLoading, setActiveMenuId]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        <CadenceDataTable
          onRowClick={(row:any) => handleOpenLink(row.template_id)}
          columns={columns}
          data={reduxTemplates || []}
          loading={templatesLoading}
          columnVisibilityKey="template_list_table"
          defaultVisible={{
            project_name: true,
            project_description: true,
            created_at: true,
            created_by: true,
            action: true,
          }}
          columnWeights={{
            project_name: 25,
            project_description: 35,
            created_at: 20,
            created_by: 10,
            action: 10,
          }}
          emptyMessage={
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? `No templates found matching "${searchTerm}"`
                  : "No templates found"}
              </p>
            </div>
          }
        />
      </div>

      {/* Load More button */}
      {hasMore && !templatesLoading && (
        <div className="flex justify-center mt-2 mb-4">
          <button
            className={`flex items-center justify-center gap-1 px-4 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 border ${
              templatesLoadingMore
                ? "bg-gray-100 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600"
                : "bg-white hover:bg-gray-50 text-blue-600 border-blue-600 hover:border-blue-700 dark:bg-slate-700 dark:text-blue-400 dark:border-blue-500 dark:hover:bg-slate-600"
            }`}
            onClick={handleLoadMore}
            disabled={templatesLoadingMore}
          >
            {templatesLoadingMore ? (
              <>
                <svg
                  className="animate-spin h-3.5 w-3.5 mr-1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Loading...
              </>
            ) : (
              <>
                Load More
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ml-1"
                >
                  <path d="M7 10l5 5 5-5" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}

      {!hasMore &&
        reduxTemplates.length > 0 &&
        !templatesLoading &&
        !templatesLoadingMore && (
          <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400 mt-2">
            End of template list
          </div>
        )}
    </div>
  );
};

export default TemplatesList;

